import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Download, Users, Activity, Calendar } from "lucide-react";
import { LIVE_STATS } from "../mock";
import { useServerStats } from "../hooks/useApi";
import { useContentObject } from "../hooks/useContentData";
import { contentFallback } from "../contentDefaults";

/* Cinematic hero with parallax + embers */
const Hero = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const { data: liveStats } = useServerStats();
  const { data: hero } = useContentObject("home.hero", contentFallback("home.hero"));
  const { data: theme } = useContentObject("theme.branding", contentFallback("theme.branding"));
  const heroBg = theme?.bg_hero || "https://images.unsplash.com/photo-1585588985526-9459c1bb9e9e?auto=format&fit=crop&w=1920&q=85";
  const overlayPct = Math.min(100, Math.max(0, Number(theme?.bg_hero_overlay ?? 55)));
  const overlayTop = overlayPct / 100;
  const overlayMid = (overlayPct * 0.76) / 100;
  const heroLogo = (hero?.logo_url || "").trim();
  const logoMaxH = Math.min(420, Math.max(80, Number(hero?.logo_max_height) || 180));

  // Combina datos de API + fallback mock
  const stats = {
    online: liveStats?.online ?? LIVE_STATS.online,
    peak: liveStats?.peak_today ?? LIVE_STATS.peak,
    registered: liveStats?.total_accounts ?? LIVE_STATS.registered,
    uptime: liveStats?.uptime ?? LIVE_STATS.uptime,
  };

  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMouse({ x, y });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  useEffect(() => {
    const target = new Date(hero?.opening_iso || contentFallback("home.hero")?.opening_iso || Date.now()).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = Math.max(target - now, 0);
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff / 3600000) % 24);
      const m = Math.floor((diff / 60000) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setCountdown({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [hero?.opening_iso]);

  // Generate ember particles
  const embers = Array.from({ length: 28 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 14,
    duration: 10 + Math.random() * 12,
    size: 1 + Math.random() * 3,
  }));

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#0a0a0d] flex items-center pt-32 pb-20">
      {/* Background image with parallax */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-300 ease-out"
        style={{
          backgroundImage: `url(${heroBg})`,
          transform: `scale(1.1) translate(${mouse.x * -16}px, ${mouse.y * -10}px)`,
        }}
      />
      {/* Vignette + gradient overlays (oscuridad editable: theme.branding.bg_hero_overlay) */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, rgba(10,10,13,${overlayTop}), rgba(10,10,13,${overlayMid}), #0a0a0d)`,
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at center, transparent 0%, rgba(10,10,13,${Math.min(1, overlayTop * 0.9)}) 75%)`,
        }}
      />
      <div className="grain" />

      {/* Embers */}
      {embers.map((e) => (
        <span
          key={e.id}
          className="ember"
          style={{
            left: `${e.left}%`,
            bottom: "-10px",
            width: `${e.size}px`,
            height: `${e.size}px`,
            animationDelay: `${e.delay}s`,
            animationDuration: `${e.duration}s`,
          }}
        />
      ))}

      {/* Decorative sigil */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ transform: `translate(-50%, -50%) translate(${mouse.x * 14}px, ${mouse.y * 14}px)` }}
      >
        <div className="sigil animate-spin-slow opacity-40" style={{ width: 720, height: 720 }} />
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-25">
        <div className="sigil" style={{ width: 460, height: 460 }} />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 w-full">
        <div className="flex flex-col items-center text-center">
          <div className="ornament animate-fade-in">
            <span>{hero?.eyebrow || "Interlude · SubAcu Base +5"}</span>
          </div>

          {heroLogo ? (
            <div className="mt-6 animate-fade-up flex justify-center w-full max-w-3xl px-2">
              <img
                src={heroLogo}
                alt={hero?.title_2 || hero?.title_1 || "Logo"}
                className="w-auto max-w-full object-contain drop-shadow-[0_8px_32px_rgba(212,168,67,0.25)]"
                style={{ maxHeight: `${logoMaxH}px` }}
              />
            </div>
          ) : (
            <h1 className="mt-6 font-display font-black text-[clamp(2.6rem,7vw,6.4rem)] leading-[0.95] tracking-[0.02em] animate-fade-up">
              <span className="block text-[#f0ebe0]">{hero?.title_1 || "La Era de"}</span>
              <span
                className="block bg-gradient-to-b from-[#f5e9c2] via-[#d4a843] to-[#8b6914] bg-clip-text text-transparent"
                style={{
                  filter:
                    "drop-shadow(0 4px 24px rgba(212,168,67,0.35)) drop-shadow(0 0 60px rgba(212,168,67,0.15))",
                }}
              >
                {hero?.title_2 || "AETHERIS"}
              </span>
            </h1>
          )}

          <p
            className="mt-6 max-w-2xl text-[#b8b3a8] text-base md:text-lg leading-relaxed animate-fade-up"
            style={{ animationDelay: "120ms" }}
          >
            {hero?.subtitle || "La nueva era del Lineage 2 ha comenzado."}
          </p>

          {/* Countdown */}
          <div
            className="mt-10 flex items-center gap-3 md:gap-5 animate-fade-up"
            style={{ animationDelay: "260ms" }}
          >
            {[
              { v: countdown.d, l: "Días" },
              { v: countdown.h, l: "Horas" },
              { v: countdown.m, l: "Min" },
              { v: countdown.s, l: "Seg" },
            ].map((c, i) => (
              <div key={i} className="flex flex-col items-center">
                <div
                  className="relative w-16 md:w-24 h-16 md:h-24 flex items-center justify-center backdrop-blur-md"
                  style={{
                    background: hero?.cd_bg || "rgba(17,17,24,0.75)",
                    borderStyle: "solid",
                    borderWidth: `${Math.max(0, Number(hero?.cd_border_width ?? 1) || 0)}px`,
                    borderColor: hero?.cd_border_color || "rgba(212,168,67,0.25)",
                    borderRadius: `${Math.max(0, Number(hero?.cd_radius ?? 6) || 0)}px`,
                    boxShadow: hero?.cd_shadow || "none",
                  }}
                >
                  {hero?.cd_shimmer !== false && (
                    <div
                      className="absolute inset-0 animate-shimmer opacity-40 pointer-events-none"
                      style={{ borderRadius: "inherit" }}
                    />
                  )}
                  <span
                    className="relative font-display font-extrabold text-2xl md:text-4xl"
                    style={{ color: hero?.cd_digit_color || "#d4a843" }}
                  >
                    {String(c.v).padStart(2, "0")}
                  </span>
                </div>
                <span
                  className="mt-2 text-[10px] md:text-xs tracking-[0.3em] uppercase"
                  style={{ color: hero?.cd_label_color || "#8a8a92" }}
                >
                  {c.l}
                </span>
              </div>
            ))}
          </div>

          <div
            className="mt-3 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2 animate-fade-up"
            style={{
              animationDelay: "300ms",
              color: hero?.date_color || "#8a8a92",
              background: hero?.date_bg || "transparent",
              borderStyle: "solid",
              borderWidth: `${Math.max(0, Number(hero?.date_border_width ?? 0) || 0)}px`,
              borderColor: hero?.date_border_color || "transparent",
              borderRadius: `${Math.max(0, Number(hero?.date_radius ?? 4) || 0)}px`,
              padding: hero?.date_padding || undefined,
            }}
          >
            <Calendar
              className="w-3.5 h-3.5 shrink-0"
              style={{ color: hero?.date_icon_color || "#d4a843" }}
            />
            {[hero?.opening_label || "Grand Opening", hero?.opening_date_text || ""].filter(Boolean).join(" · ")}
          </div>

          {/* CTAs */}
          <div
            className="mt-10 flex flex-col sm:flex-row items-center gap-4 animate-fade-up"
            style={{ animationDelay: "380ms" }}
          >
            <Link to={hero?.cta_primary_link || "/downloads"} className="btn-gold px-9 py-4 text-sm rounded-sm flex items-center gap-2 animate-glow">
              <Download className="w-4 h-4" /> {hero?.cta_primary_text || "Descargar Cliente"}
            </Link>
            <Link to={hero?.cta_secondary_link || "/wiki"} className="btn-ghost px-9 py-4 text-sm rounded-sm flex items-center gap-2">
              <Play className="w-4 h-4 text-[#d4a843]" /> {hero?.cta_secondary_text || "Server Info"}
            </Link>
          </div>

          {/* Live ticker */}
          <div
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full max-w-4xl animate-fade-up"
            style={{ animationDelay: "500ms" }}
          >
            <Stat label="Online ahora" value={stats.online} icon={<Activity />} accent />
            <Stat label="Pico de jugadores" value={stats.peak} icon={<Users />} />
            <Stat label="Registrados" value={stats.registered} icon={<Users />} />
            <Stat label="Uptime" value={stats.uptime} icon={<Activity />} text />
          </div>
        </div>
      </div>

      {/* Bottom fade to next section */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-[#0a0a0d] pointer-events-none" />
    </section>
  );
};

const Stat = ({ label, value, icon, accent, text }) => {
  const [display, setDisplay] = useState(typeof value === "number" ? 0 : value);
  useEffect(() => {
    if (typeof value !== "number") return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min((t - start) / 1400, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.floor(value * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <div className={`glass rounded-sm p-4 md:p-5 flex items-center gap-4 transition-all hover:translate-y-[-2px] ${accent ? "border-[rgba(212,168,67,0.4)]" : ""}`}>
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${accent ? "bg-[rgba(212,168,67,0.15)] text-[#f5c542]" : "bg-[#21212b] text-[#b8b3a8]"}`}>
        {React.cloneElement(icon, { className: "w-4 h-4" })}
      </div>
      <div className="flex flex-col min-w-0">
        <span className="font-display font-bold text-lg md:text-2xl text-[#f0ebe0] truncate">
          {text ? value : typeof value === "number" ? display.toLocaleString() : value}
        </span>
        <span className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-[#8a8a92]">
          {label}
        </span>
      </div>
    </div>
  );
};

export default Hero;
